import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ToastComponent from "../../components/toast-component";
import ProcessingDialog from "../../components/processing-dialog";
import "../../utils/i18n/i18n";

const UiFeedbackContext = createContext(null);

/**
 * Global toast + processing overlay. Each is controlled separately:
 * - showToast / hideToast → only ToastComponent
 * - showProcessing / hideProcessing → only ProcessingDialog
 * Calling one does not affect the other.
 */
export function UiFeedbackProvider({ children }) {
  const { t } = useTranslation();

  const [processingVisible, setProcessingVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");

  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("warning");

  const toastTimerRef = useRef(null);

  const hideToast = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToastVisible(false);
  }, []);

  const showToast = useCallback((message, type = "warning", durationMs = 3000) => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => {
      setToastVisible(false);
      toastTimerRef.current = null;
    }, durationMs);
  }, []);

  const showProcessing = useCallback((message) => {
    setProcessingMessage(message ?? t("alert_procesando_solicitud"));
    setProcessingVisible(true);
  }, [t]);

  const hideProcessing = useCallback(() => {
    setProcessingVisible(false);
  }, []);

  const value = {
    showToast,
    hideToast,
    showProcessing,
    hideProcessing,
  };

  return (
    <UiFeedbackContext.Provider value={value}>
      {children}
      <ProcessingDialog visible={processingVisible} message={processingMessage} />
      <ToastComponent
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onClose={hideToast}
      />
    </UiFeedbackContext.Provider>
  );
}

function useUiFeedbackContext() {
  const context = useContext(UiFeedbackContext);
  if (!context) {
    throw new Error("useUiFeedback must be used within UiFeedbackProvider");
  }
  return context;
}

/** Toast only — does not show or hide the processing dialog. */
export function useToast() {
  const { showToast, hideToast } = useUiFeedbackContext();
  return { showToast, hideToast };
}

/** Processing overlay only — does not show or hide the toast. */
export function useProcessing() {
  const { showProcessing, hideProcessing } = useUiFeedbackContext();
  return { showProcessing, hideProcessing };
}

/** Both toast and processing (same as using useToast + useProcessing). */
export function useUiFeedback() {
  return useUiFeedbackContext();
}
